// Print on web, share a PDF on iOS — from one HTML source.
//
// PlanGameView builds two printable documents as HTML strings (the Game Plan,
// landscape, and the Lineup Card, portrait) and handed them to
// `window.open(...).print()`. That is a web-only pattern: in the Capacitor
// WKWebView `window.print()` does nothing, so both buttons were hidden behind
// `!Capacitor.isNativePlatform()` and an iOS coach could not get a game plan out
// of the app at all.
//
// The important constraint is that native must NOT get its own layout. Keeping
// two renderings of the same document in sync is the failure mode that produced
// the GameChanger innings and steals bugs, so both platforms render the exact
// same HTML string and only the handoff differs:
//
//   WEB     unchanged: a popup window, then print().
//   NATIVE  render the HTML in an off-screen iframe (its own document, so the
//           document's global CSS — `*`, `body`, `@page` — cannot leak into the
//           app), rasterise it, compose a PDF, and open the OS share sheet.
//           AirPrint, Messages, Mail and Save to Files all come free for a PDF.
//
// Share handoff mirrors src/lib/field-time-pdf.ts.

import { Capacitor } from '@capacitor/core';

/** US letter in points. */
const LETTER = { portrait: { w: 612, h: 792 }, landscape: { w: 792, h: 612 } };
/**
 * Iframe width at 96dpi, matching the printable area the documents' own
 * `@page` rule declares (letter minus 0.45in side margins). `@page` has no
 * effect on screen, so the iframe has to reproduce that width or the layout
 * differs from what print produces.
 */
const CONTENT_PX = { portrait: { w: 816 - 86 }, landscape: { w: 1056 - 86 } };
/** Printed margin, in points. Matches the 0.5in/0.45in in the documents. */
const MARGIN = { top: 36, side: 32 };

export interface PrintOrShareOptions {
  /** Downloaded / shared file name, without extension. */
  fileName: string;
  /** Share-sheet title. */
  title: string;
  orientation: 'portrait' | 'landscape';
  /** Popup size for the web path, matching the previous hand-tuned values. */
  webWindow?: { width: number; height: number };
}

/**
 * Render `html` into a detached iframe and hand back its body once loaded.
 * The iframe is same-origin via srcdoc, so its document is readable.
 */
async function renderOffscreen(
  html: string,
  widthPx: number,
): Promise<{ body: HTMLElement; cleanup: () => void }> {
  const iframe = document.createElement('iframe');
  // Off-screen rather than display:none — a hidden iframe does not lay out, and
  // an unlaid-out document rasterises blank. Height is generous and the capture
  // uses the content's real height, so nothing is cut off vertically either.
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText = `position:fixed;left:-10000px;top:0;border:0;width:${widthPx}px;height:2000px;`;
  iframe.srcdoc = html;
  document.body.appendChild(iframe);

  const cleanup = () => { try { iframe.remove(); } catch { /* already gone */ } };

  await new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('Timed out preparing the document')), 10000);
    iframe.onload = () => { clearTimeout(timer); resolve(); };
    iframe.onerror = () => { clearTimeout(timer); reject(new Error('Could not prepare the document')); };
  });

  const win = iframe.contentWindow;
  const docu = iframe.contentDocument;
  if (!win || !docu?.body) { cleanup(); throw new Error('Could not read the prepared document'); }

  // Web fonts inside the iframe load independently of the host page; capturing
  // before they land produces fallback-font output.
  try { await (docu as any).fonts?.ready; } catch { /* not supported, carry on */ }
  // One frame so layout settles before rasterising.
  await new Promise<void>((r) => win.requestAnimationFrame(() => r()));

  return { body: docu.body, cleanup };
}

/**
 * Print (web) or share a PDF (native). Rejects on failure so the caller can
 * surface it: a silent no-op on the share button is exactly the dead end this
 * replaces.
 */
export async function printOrShareHtml(html: string, opts: PrintOrShareOptions): Promise<void> {
  const { fileName, title, orientation } = opts;

  if (!Capacitor.isNativePlatform()) {
    const { width = 900, height = 700 } = opts.webWindow ?? {};
    const win = window.open('', '_blank', `width=${width},height=${height}`);
    if (!win) throw new Error('Your browser blocked the print window. Allow popups and try again.');
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => { try { win.print(); } catch { /* user dismissed */ } }, 300);
    return;
  }

  const pt = LETTER[orientation];
  const { body, cleanup } = await renderOffscreen(html, CONTENT_PX[orientation].w);

  try {
    const { toJpeg } = await import('html-to-image');
    const { jsPDF } = await import('jspdf');

    // Capture the document at its REAL size. Passing fixed width/height to
    // toJpeg crops rather than scales, which is what cut the first version of
    // this off after four innings: the game plan lays out wider than a letter
    // page and print silently shrinks it to fit. Measure, then scale, so the
    // whole plan lands on the page the way printing it would.
    const w = Math.max(body.scrollWidth, body.offsetWidth, 1);
    const h = Math.max(body.scrollHeight, body.offsetHeight, 1);

    // JPEG for the same reason as the Field Time Report: a mostly-white
    // document compresses far smaller with no visible loss, and this file has
    // to travel by Messages or Mail.
    const jpeg = await toJpeg(body, {
      width: w,
      height: h,
      pixelRatio: 2,
      quality: 0.92,
      backgroundColor: '#FFFFFF',
      cacheBust: true,
      style: { margin: '0' },
    });

    const pdf = new jsPDF({ unit: 'pt', format: 'letter', orientation });

    // Fit inside the printable area, preserving aspect ratio, and centre what
    // is left over. The upper bound is 72/96, NOT 1: the capture is in CSS
    // pixels at 96dpi and the PDF is in points at 72dpi, so a scale of 1 would
    // draw a narrow document a third larger than its true physical size. This
    // clamp means "never bigger than it would print at natural size".
    const availW = pt.w - MARGIN.side * 2;
    const availH = pt.h - MARGIN.top * 2;
    const NATURAL = 72 / 96;
    const scale = Math.min(availW / w, availH / h, NATURAL);
    const drawW = w * scale;
    const drawH = h * scale;
    pdf.addImage(
      jpeg, 'JPEG',
      MARGIN.side + (availW - drawW) / 2,
      MARGIN.top + (availH - drawH) / 2,
      drawW, drawH,
    );

    const { Filesystem, Directory } = await import('@capacitor/filesystem');
    const { Share } = await import('@capacitor/share');
    const name = `${fileName.replace(/[^a-z0-9]+/gi, '-').toLowerCase().replace(/(^-|-$)/g, '') || 'dugout'}.pdf`;
    const written = await Filesystem.writeFile({
      path: name,
      data: pdf.output('datauristring').split(',')[1],
      directory: Directory.Cache,
    });
    await Share.share({ title, url: written.uri, dialogTitle: title });
  } finally {
    cleanup();
  }
}
